/**
 * ReclamationSuggestionsPanel — Day Diary v2 fallback-batch suggestion list
 * (task 237, D2).
 *
 * Once the diary completes (AC2), the fallback batch (`projectId === null`)
 * renders here: a header plus one {@link SuggestionCard} per pending
 * reclamation suggestion. The cards POST accept/dismiss to task 236's routes
 * via the callbacks the parent threads down.
 *
 * The `canViewScreenshots` prop flows straight through to each card — it is
 * the §2.7 OCR-exposure gate (AC9). Phase 1 the parent passes the org-member
 * default (true).
 */

import { SuggestionCard } from './SuggestionCard';
import type { DiarySuggestion } from '@/types/dayDiaryV2';
import styles from './ReclamationSuggestionsPanel.module.css';

export interface ReclamationSuggestionsPanelProps {
  suggestions: DiarySuggestion[];
  canViewScreenshots: boolean;
  tz: string;
  onAccept: (suggestion: DiarySuggestion) => void;
  onDismiss: (suggestion: DiarySuggestion) => void;
  onEditBeforeAccepting?: (suggestion: DiarySuggestion) => void;
  onViewScreenshot?: (suggestion: DiarySuggestion) => void;
  onMarkForReview?: (suggestion: DiarySuggestion) => void;
  /** Suggestion id currently mutating — disables that card's actions. */
  busyId?: string | null;
}

export function ReclamationSuggestionsPanel({
  suggestions,
  canViewScreenshots,
  tz,
  onAccept,
  onDismiss,
  onEditBeforeAccepting,
  onViewScreenshot,
  onMarkForReview,
  busyId = null,
}: ReclamationSuggestionsPanelProps) {
  return (
    <section className={styles.panel} data-testid="reclamation-suggestions-panel">
      <div className={styles.header}>
        <h2 className={styles.heading}>Worth a second look</h2>
        <p className={styles.subhead}>
          A few moments I couldn&rsquo;t place. Accept the ones that fit, dismiss the rest.
        </p>
      </div>
      {suggestions.length === 0 ? (
        <p className={styles.empty}>Nothing left to review here.</p>
      ) : (
        <div className={styles.cards}>
          {suggestions.map((s) => (
            <SuggestionCard
              key={s.id}
              suggestion={s}
              canViewScreenshots={canViewScreenshots}
              tz={tz}
              busy={busyId === s.id}
              onAccept={s.variant === 'unattributable' ? undefined : onAccept}
              onDismiss={onDismiss}
              onEditBeforeAccepting={
                s.variant === 'new_activity' ? onEditBeforeAccepting : undefined
              }
              onViewScreenshot={onViewScreenshot}
              onMarkForReview={s.variant === 'unattributable' ? onMarkForReview : undefined}
            />
          ))}
        </div>
      )}
    </section>
  );
}
